import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Share, Plus, X } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'pwa-install-dismissed'

function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches
    || ('standalone' in navigator && (navigator as Record<string, unknown>)['standalone'] === true)
}

export function PwaInstallPrompt() {
  const { t } = useTranslation()
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showAndroid, setShowAndroid] = useState(false)
  const [showIos, setShowIos] = useState(false)

  useEffect(() => {
    // Already installed as PWA
    if (isInStandaloneMode()) return

    // Don't show if user already dismissed recently (7 days)
    const dismissed = localStorage.getItem(DISMISSED_KEY)
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return

    if (isIos()) {
      // iOS: show guide after a short delay
      const timer = setTimeout(() => setShowIos(true), 2000)
      return () => clearTimeout(timer)
    }

    // Android/Desktop: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowAndroid(true)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') setShowAndroid(false)
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setShowAndroid(false)
    setShowIos(false)
    localStorage.setItem(DISMISSED_KEY, Date.now().toString())
  }

  // ── Android / Desktop banner ──
  if (showAndroid) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[90] animate-[slideUp_0.3s_ease-out] pb-[max(env(safe-area-inset-bottom,16px),16px)]">
        <div className="mx-4 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-xl ring-1 ring-black/5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#79305d]/10">
            <Download className="h-5 w-5 text-[#79305d]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-neutral-900">{t('pwa.install.title')}</p>
            <p className="text-xs leading-relaxed text-neutral-500">{t('pwa.install.message')}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={handleDismiss}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-neutral-400 transition-colors active:bg-neutral-100"
            >
              {t('pwa.install.dismiss')}
            </button>
            <button
              onClick={handleInstall}
              className="rounded-lg bg-[#79305d] px-4 py-1.5 text-xs font-bold text-white shadow-sm transition-transform active:scale-95"
            >
              {t('pwa.install.button')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── iOS guide ──
  if (showIos) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[90] animate-[slideUp_0.3s_ease-out] pb-[max(env(safe-area-inset-bottom,16px),16px)]">
        <div className="relative mx-4 rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/5">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-neutral-100 text-neutral-400"
          >
            <X className="h-3.5 w-3.5" />
          </button>

          <p className="mb-3 pr-8 text-sm font-bold text-neutral-900">{t('pwa.install.iosTitle')}</p>

          {/* Step-by-step guide */}
          <div className="flex flex-col gap-3">
            {/* Step 1: Tap Share */}
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50">
                <Share className="h-4 w-4 text-blue-500" />
              </div>
              <p className="text-xs leading-relaxed text-neutral-600">
                1. Appuyez sur <span className="font-semibold text-neutral-800">Partager</span> en bas de Safari
              </p>
            </div>
            {/* Step 2: Add to Home Screen */}
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                <Plus className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="text-xs leading-relaxed text-neutral-600">
                2. Choisissez <span className="font-semibold text-neutral-800">Sur l'écran d'accueil</span>
              </p>
            </div>
          </div>

          {/* Arrow pointing down to Safari bar */}
          <div className="mt-3 flex justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-neutral-300">
              <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <button
            onClick={handleDismiss}
            className="mt-2 w-full rounded-xl bg-[#79305d] py-2.5 text-xs font-bold text-white transition-transform active:scale-[0.98]"
          >
            {t('pwa.install.iosOk')}
          </button>
        </div>
      </div>
    )
  }

  return null
}
